<template>
	<div class="mobile-single-filter">
		<div class="mobile-single-filter__title">{{ i18nService.$tDynamic('modules.cars_module.cars.filters', filter.title) }}</div>
		<div v-if="filter.filterName === EFilterNames.GROUP && allGroups" class="mobile-single-filter__values-container">
			<div v-for="(item, i) in allGroups" :key="i" class="mobile-single-filter__value">
				<GlobalCheckbox
					v-if="item"
					v-model="selectedGroups"
					readonly
					:value="item.name"
					@click="urlFiltersRef.toggleArrayFilter('group', item.name)">
					{{
						typeof item.name === 'string'
							? i18nService.$tDynamic('data.group_map_translations', groupMapTranslations[item.name])
							: item.name
					}}
				</GlobalCheckbox>
			</div>
		</div>
		<div v-else-if="!filter.isSeats && filteredParameterValues.length" class="mobile-single-filter__values-container">
			<div v-for="(item, i) in filteredParameterValues" :key="i" class="mobile-single-filter__value">
				<GlobalCheckbox v-if="parameter" v-model="selectedValues" readonly :value="item" @click="handleParameterToggle(item)">{{
					typeof item === 'string' ? i18nService.$tDynamic('data.group_map_translations', parametersMapTranslations[item]) : item
				}}</GlobalCheckbox>
			</div>
		</div>
		<div v-else-if="filter.isSort" class="mobile-single-filter__values-container">
			<v-radio-group :model-value="currentSortItem" hide-details>
				<GlobalRadio
					v-for="(item, i) in filter.items"
					:key="i"
					class="cars-filter__filter-menu-radio"
					readonly
					density="compact"
					color="var(--primary-color)"
					:label="i18nService.$tDynamic('modules.cars_module.cars.filters', item.name)"
					:value="item"
					@click="urlFiltersRef.setSort(item.value as ESortValues)" />
			</v-radio-group>
		</div>
		<div v-else-if="filter.isSeats" class="mobile-single-filter__values">
			<v-slider
				v-model="seatsSliderValue"
				name="Liczba miejsc"
				class="mobile-single-filter__slider"
				:ticks="seatsLabels"
				:step="1"
				hide-details
				show-ticks="always"
				tick-size="0"
				thumb-color="var(--primary-color)"
				track-fill-color="var(--primary-color)"
				track-color="var(--stroke-on-background)"
				:track-size="4"
				:min="1"
				:thumb-size="16"
				:max="5" />
		</div>
	</div>
</template>

<script setup lang="ts">
import { EFilterNames, ESortValues } from '@/modules/carsModule/types/filters'
import type { useUrlFilters } from '@/modules/carsModule/composables/useUrlFilters'

const urlFiltersRef = inject<ReturnType<typeof useUrlFilters>>('urlFilters')!
const carsStore = useCarsStore()
const i18nService = usei18nService()
const $t = useI18n().t

export interface ISingleFilterProps {
	title: string
	filterName?: EFilterNames
	items?: {
		name: string
		value: string
	}[]
	disabled?: boolean
	isSeats?: boolean
	isSort?: boolean
}

const componentProps = defineProps<{
	filter: ISingleFilterProps
}>()

const parameter = computed(() => {
	if (componentProps.filter.filterName) {
		return carsStore.getFilterByParameterName(componentProps.filter.filterName)
	}
	return undefined
})

const filteredParameterValues = computed(() => filterPublicParameterValues(componentProps.filter.filterName, parameter.value?.values))

const allGroups = computed(() => filterPublicGroups(carsStore.getAllGroups ?? []))

// Derive selected state from URL
const selectedValues = computed<string[]>(() => {
	const s = urlFiltersRef.state.value
	if (componentProps.filter.filterName === EFilterNames.TRANSMISSION) return s.transmission
	if (componentProps.filter.filterName === EFilterNames.FUEL_TYPE) return s.fuelType
	return []
})

const selectedGroups = computed<string[]>(() => urlFiltersRef.state.value.groups)

const currentSortItem = computed(() => {
	const sort = urlFiltersRef.state.value.sort
	if (!sort || !componentProps.filter.items) return undefined
	return componentProps.filter.items.find((item) => item.value === sort)
})

// Seats: map slider position ↔ actual min seats value
const SEATS_SLIDER_MAP: Record<number, number | null> = { 1: null, 2: 2, 3: 4, 4: 5, 5: 7 }
const SEATS_REVERSE_MAP: Record<number, number> = { 2: 2, 4: 3, 5: 4, 7: 5 }

const seatsSliderValue = computed({
	get() {
		const min = urlFiltersRef.state.value.seatsMin
		if (min == null) return 1
		return SEATS_REVERSE_MAP[min] ?? 1
	},
	set(val: number) {
		urlFiltersRef.setSeatsMin(SEATS_SLIDER_MAP[val] ?? null)
	},
})

const seatsLabels = {
	1: $t('common.any_short'),
	2: '2+',
	3: '4+',
	4: '5+',
	5: '7+',
}

function handleParameterToggle(item: string | number) {
	if (componentProps.filter.filterName === EFilterNames.TRANSMISSION) {
		urlFiltersRef.toggleArrayFilter('transmission', String(item))
	} else if (componentProps.filter.filterName === EFilterNames.FUEL_TYPE) {
		urlFiltersRef.toggleArrayFilter('fuel_type', String(item))
	}
}
</script>

<style lang="scss" scoped src="./SingleFilter.scss"></style>
